import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useOutlet } from '../contexts/OutletContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { useQZPrint } from '../contexts/QZPrintContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  Store,
  Phone,
  MapPin,
  Save,
  Plus,
  Loader2,
  CreditCard,
  UtensilsCrossed,
  Package,
  Bike,
  Printer,
  Trash2,
  Edit,
  Wifi,
  Usb,
  Play,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  Receipt,
  Percent,
  Tag,
  Star,
  Gift,
  Award,
  Globe,
  DollarSign,
  Settings2,
  WifiOff,
} from 'lucide-react';
import { LanguageSwitcher, TaxCurrencySettings, BackupRestoreSettings } from '../components/settings';
import { TranslationSettings } from '../components/settings/TranslationSettings';
import { UpdateSettings } from '../components/settings/UpdateSettings';
import { PrinterSetupWizard } from '../components/settings/PrinterSetupWizard';
import { QZPrinterConfig } from '../components/settings/QZPrinterConfig';
import { SettingsSidebar, MobileSettingsSidebar, getSettingsMenuItems } from '../components/settings/SettingsSidebar';
import { FileText, Database, Building2 } from 'lucide-react';

export default function Settings() {
  const { t } = useTranslation();
  const { api, user } = useAuth();
  const { outlets, currentOutlet, createOutlet, fetchOutlets } = useOutlet();
  const { formatCurrency, refreshCurrency } = useCurrency();
  const { isConnected: qzConnected, printerConfig: qzPrinterConfig } = useQZPrint();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('outlet');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [outletForm, setOutletForm] = useState({
    name: '',
    address: '',
    phone: '',
    is_active: true,
  });
  const [newOutletForm, setNewOutletForm] = useState({
    name: '',
    address: '',
    phone: '',
  });
  const [paymentSettings, setPaymentSettings] = useState({
    dine_in_mode: 'POSTPAID',
    takeaway_mode: 'PREPAID',
    delivery_mode: 'PREPAID',
    allow_override: true,
  });

  // Printer management state
  const [printers, setPrinters] = useState([]);
  const [routings, setRoutings] = useState([]);
  const [printJobs, setPrintJobs] = useState([]);
  const [printerDialogOpen, setPrinterDialogOpen] = useState(false);
  const [printerWizardOpen, setPrinterWizardOpen] = useState(false);
  const [editingPrinter, setEditingPrinter] = useState(null);
  const [printerForm, setPrinterForm] = useState({
    name: '',
    printer_role: 'KITCHEN',
    connection_type: 'LAN',
    ip_address: '',
    port: 9100,
    usb_vendor_id: '',
    usb_product_id: '',
    is_active: true,
  });

  // Receipt settings state
  const [receiptSettings, setReceiptSettings] = useState({
    receipt_printer_id: null,
    auto_print_on_payment: false,
    show_tax_breakdown: true,
    footer_text: 'Terima kasih atas kunjungan Anda!',
  });

  // Discount Presets state
  const [discountPresets, setDiscountPresets] = useState([]);
  const [discountSettings, setDiscountSettings] = useState({
    max_cashier_discount_percent: 10,
    max_cashier_discount_amount: 50000,
  });
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [editingPreset, setEditingPreset] = useState(null);
  const [presetForm, setPresetForm] = useState({
    name: '',
    discount_type: 'PERCENT',
    value: '',
    reason: '',
    display_order: 0,
    color: 'orange',
    is_scheduled: false,
    schedule_days: [],
    schedule_start_time: '',
    schedule_end_time: '',
  });

  const dayNames = ['Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu', 'Minggu'];

  // Loyalty state
  const [loyaltySettings, setLoyaltySettings] = useState({
    is_enabled: false,
    points_per_amount: 1000,
    points_multiplier: 1.0,
    min_transaction_for_points: 0,
    points_expiry_days: 365,
  });
  const [loyaltyRewards, setLoyaltyRewards] = useState([]);
  const [rewardDialogOpen, setRewardDialogOpen] = useState(false);
  const [editingReward, setEditingReward] = useState(null);
  const [rewardForm, setRewardForm] = useState({
    name: '',
    description: '',
    reward_type: 'DISCOUNT_FIXED',
    reward_value: '',
    menu_item_name: '',
    points_required: '',
    display_order: 0,
  });

  // Customer Tags state
  const [customerTags, setCustomerTags] = useState([]);
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [tagForm, setTagForm] = useState({
    name: '',
    color: 'gray',
    description: '',
    discount_percent: '',
  });
  const tagColors = ['gray', 'blue', 'green', 'yellow', 'red', 'purple', 'orange'];

  useEffect(() => {
    if (currentOutlet) {
      setOutletForm({
        name: currentOutlet.name,
        address: currentOutlet.address || '',
        phone: currentOutlet.phone || '',
        is_active: currentOutlet.is_active,
      });
      fetchPaymentSettings();
      fetchPrinters();
      fetchRoutings();
      fetchPrintJobs();
      fetchReceiptSettings();
      fetchDiscountPresets();
      fetchDiscountSettings();
      fetchLoyaltySettings();
      fetchLoyaltyRewards();
      fetchCustomerTags();
    }
  }, [currentOutlet]);

  // Auto-refresh printer status every 15 seconds
  useEffect(() => {
    if (!currentOutlet?.id) return;
    
    const interval = setInterval(() => {
      fetchPrinters();
    }, 15000);
    
    return () => clearInterval(interval);
  }, [currentOutlet?.id]);

  const fetchPaymentSettings = async () => {
    if (!currentOutlet?.id) return;
    try {
      const resp = await api.get('/settings/payment-mode', {
        params: { outlet_id: currentOutlet.id },
      });
      setPaymentSettings(resp.data);
    } catch (error) {
      console.error('Failed to fetch payment settings:', error);
    }
  };

  // Printer functions
  const fetchPrinters = async () => {
    if (!currentOutlet?.id) return;
    try {
      // Use status endpoint to get printers with status and stale check
      const resp = await api.get('/printers/status', {
        params: { outlet_id: currentOutlet.id },
      });
      setPrinters(resp.data);
    } catch (error) {
      console.error('Failed to fetch printers:', error);
    }
  };

  const fetchRoutings = async () => {
    if (!currentOutlet?.id) return;
    try {
      const resp = await api.get('/printer-routing', {
        params: { outlet_id: currentOutlet.id },
      });
      setRoutings(resp.data);
    } catch (error) {
      console.error('Failed to fetch routings:', error);
    }
  };

  const fetchPrintJobs = async () => {
    if (!currentOutlet?.id) return;
    try {
      const resp = await api.get('/print-jobs', {
        params: { outlet_id: currentOutlet.id, limit: 20 },
      });
      setPrintJobs(resp.data);
    } catch (error) {
      console.error('Failed to fetch print jobs:', error);
    }
  };

  const fetchReceiptSettings = async () => {
    if (!currentOutlet?.id) return;
    try {
      const resp = await api.get('/settings/receipt', {
        params: { outlet_id: currentOutlet.id },
      });
      setReceiptSettings(resp.data);
    } catch (error) {
      console.error('Failed to fetch receipt settings:', error);
    }
  };

  const handleUpdateReceiptSettings = async () => {
    if (!currentOutlet) return;
    setLoading(true);
    try {
      await api.put('/settings/receipt', receiptSettings, {
        params: { outlet_id: currentOutlet.id },
      });
      toast.success('Receipt settings updated');
    } catch (error) {
      toast.error('Failed to update receipt settings');
    } finally {
      setLoading(false);
    }
  };

  // Discount Presets Functions
  const fetchDiscountPresets = async () => {
    if (!currentOutlet?.id) return;
    try {
      const resp = await api.get('/discount-presets/all', {
        params: { outlet_id: currentOutlet.id },
      });
      setDiscountPresets(resp.data);
    } catch (error) {
      console.error('Failed to fetch discount presets:', error);
    }
  };

  const fetchDiscountSettings = async () => {
    if (!currentOutlet?.id) return;
    try {
      const resp = await api.get('/settings/discount', {
        params: { outlet_id: currentOutlet.id },
      });
      setDiscountSettings({
        max_cashier_discount_percent: resp.data.max_cashier_discount_percent || 10,
        max_cashier_discount_amount: resp.data.max_cashier_discount_amount || 50000,
      });
    } catch (error) {
      console.error('Failed to fetch discount settings:', error);
    }
  };

  const handleSaveDiscountSettings = async () => {
    if (!currentOutlet?.id) return;
    setLoading(true);
    try {
      await api.put('/settings/discount', discountSettings, {
        params: { outlet_id: currentOutlet.id },
      });
      toast.success('Discount settings updated');
    } catch (error) {
      toast.error('Failed to update discount settings');
    } finally {
      setLoading(false);
    }
  };

  const openPresetDialog = (preset = null) => {
    if (preset) {
      setEditingPreset(preset);
      setPresetForm({
        name: preset.name,
        discount_type: preset.discount_type,
        value: preset.value.toString(),
        reason: preset.reason,
        display_order: preset.display_order,
        color: preset.color || 'orange',
        is_scheduled: preset.is_scheduled || false,
        schedule_days: preset.schedule_days || [],
        schedule_start_time: preset.schedule_start_time || '',
        schedule_end_time: preset.schedule_end_time || '',
      });
    } else {
      setEditingPreset(null);
      setPresetForm({
        name: '',
        discount_type: 'PERCENT',
        value: '',
        reason: '',
        display_order: discountPresets.length,
        color: 'orange',
        is_scheduled: false,
        schedule_days: [],
        schedule_start_time: '',
        schedule_end_time: '',
      });
    }
    setDiscountDialogOpen(true);
  };

  const toggleScheduleDay = (day) => {
    setPresetForm(prev => {
      const days = prev.schedule_days.includes(day)
        ? prev.schedule_days.filter(d => d !== day)
        : [...prev.schedule_days, day].sort();
      return { ...prev, schedule_days: days };
    });
  };

  const formatSchedule = (preset) => {
    if (!preset.is_scheduled) return 'Selalu aktif';
    const days = (preset.schedule_days || []).map(d => dayNames[d]?.substring(0, 3) || '').join(', ');
    const time = `${preset.schedule_start_time || '00:00'} - ${preset.schedule_end_time || '23:59'}`;
    return `${days} (${time})`;
  };

  const handleSavePreset = async () => {
    if (!presetForm.name.trim()) {
      toast.error('Nama preset wajib diisi');
      return;
    }
    if (!presetForm.value || parseFloat(presetForm.value) <= 0) {
      toast.error('Nilai diskon wajib diisi');
      return;
    }
    if (!presetForm.reason.trim()) {
      toast.error('Alasan diskon wajib diisi');
      return;
    }

    setLoading(true);
    try {
      if (editingPreset) {
        await api.put(`/discount-presets/${editingPreset.id}`, {
          ...presetForm,
          value: parseFloat(presetForm.value),
        });
        toast.success('Preset berhasil diperbarui');
      } else {
        await api.post('/discount-presets', {
          ...presetForm,
          value: parseFloat(presetForm.value),
        }, {
          params: { outlet_id: currentOutlet.id },
        });
        toast.success('Preset berhasil ditambahkan');
      }
      setDiscountDialogOpen(false);
      fetchDiscountPresets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan preset');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePreset = async (preset) => {
    if (!window.confirm(`Hapus preset "${preset.name}"?`)) return;
    try {
      await api.delete(`/discount-presets/${preset.id}`);
      toast.success('Preset berhasil dihapus');
      fetchDiscountPresets();
    } catch (error) {
      toast.error('Gagal menghapus preset');
    }
  };

  // Loyalty Functions
  const fetchLoyaltySettings = async () => {
    if (!currentOutlet?.id) return;
    try {
      const resp = await api.get('/settings/loyalty', { params: { outlet_id: currentOutlet.id } });
      setLoyaltySettings({
        is_enabled: resp.data.is_enabled || false,
        points_per_amount: resp.data.points_per_amount || 1000,
        points_multiplier: resp.data.points_multiplier || 1.0,
        min_transaction_for_points: resp.data.min_transaction_for_points || 0,
        points_expiry_days: resp.data.points_expiry_days || 365,
      });
    } catch (error) {
      console.error('Failed to fetch loyalty settings:', error);
    }
  };

  const fetchLoyaltyRewards = async () => {
    if (!currentOutlet?.id) return;
    try {
      const resp = await api.get('/loyalty-rewards/all', { params: { outlet_id: currentOutlet.id } });
      setLoyaltyRewards(resp.data);
    } catch (error) {
      console.error('Failed to fetch loyalty rewards:', error);
    }
  };

  const handleSaveLoyaltySettings = async () => {
    if (!currentOutlet?.id) return;
    setLoading(true);
    try {
      await api.put('/settings/loyalty', loyaltySettings, { params: { outlet_id: currentOutlet.id } });
      toast.success('Pengaturan loyalty berhasil disimpan');
    } catch (error) {
      toast.error('Gagal menyimpan pengaturan loyalty');
    } finally {
      setLoading(false);
    }
  };

  const openRewardDialog = (reward = null) => {
    if (reward) {
      setEditingReward(reward);
      setRewardForm({
        name: reward.name,
        description: reward.description || '',
        reward_type: reward.reward_type,
        reward_value: reward.reward_value.toString(),
        menu_item_name: reward.menu_item_name || '',
        points_required: reward.points_required.toString(),
        display_order: reward.display_order,
      });
    } else {
      setEditingReward(null);
      setRewardForm({
        name: '',
        description: '',
        reward_type: 'DISCOUNT_FIXED',
        reward_value: '',
        menu_item_name: '',
        points_required: '',
        display_order: loyaltyRewards.length,
      });
    }
    setRewardDialogOpen(true);
  };

  const handleSaveReward = async () => {
    if (!rewardForm.name.trim()) {
      toast.error('Nama hadiah wajib diisi');
      return;
    }
    if (!rewardForm.points_required || parseInt(rewardForm.points_required) <= 0) {
      toast.error('Poin yang dibutuhkan wajib diisi');
      return;
    }

    setLoading(true);
    try {
      const data = {
        ...rewardForm,
        reward_value: parseFloat(rewardForm.reward_value) || 0,
        points_required: parseInt(rewardForm.points_required),
      };
      
      if (editingReward) {
        await api.put(`/loyalty-rewards/${editingReward.id}`, data);
        toast.success('Hadiah berhasil diperbarui');
      } else {
        await api.post('/loyalty-rewards', data, { params: { outlet_id: currentOutlet.id } });
        toast.success('Hadiah berhasil ditambahkan');
      }
      setRewardDialogOpen(false);
      fetchLoyaltyRewards();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan hadiah');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReward = async (reward) => {
    if (!window.confirm(`Hapus hadiah "${reward.name}"?`)) return;
    try {
      await api.delete(`/loyalty-rewards/${reward.id}`);
      toast.success('Hadiah berhasil dihapus');
      fetchLoyaltyRewards();
    } catch (error) {
      toast.error('Gagal menghapus hadiah');
    }
  };

  // Customer Tags functions
  const fetchCustomerTags = async () => {
    if (!currentOutlet?.id) return;
    try {
      const resp = await api.get('/customer-tags/all', {
        params: { outlet_id: currentOutlet.id },
      });
      setCustomerTags(resp.data);
    } catch (error) {
      console.error('Failed to fetch customer tags:', error);
    }
  };

  const openTagDialog = (tag = null) => {
    setEditingTag(tag);
    if (tag) {
      setTagForm({
        name: tag.name,
        color: tag.color || 'gray',
        description: tag.description || '',
        discount_percent: tag.discount_percent || '',
      });
    } else {
      setTagForm({
        name: '',
        color: 'gray',
        description: '',
        discount_percent: '',
      });
    }
    setTagDialogOpen(true);
  };

  const handleSaveTag = async () => {
    if (!tagForm.name.trim()) {
      toast.error('Nama tag wajib diisi');
      return;
    }
    
    setLoading(true);
    try {
      const data = {
        name: tagForm.name.trim(),
        color: tagForm.color,
        description: tagForm.description || '',
        discount_percent: tagForm.discount_percent ? parseFloat(tagForm.discount_percent) : 0,
      };
      
      if (editingTag) {
        await api.put(`/customer-tags/${editingTag.id}`, data);
        toast.success('Tag berhasil diperbarui');
      } else {
        await api.post('/customer-tags', data, {
          params: { outlet_id: currentOutlet.id },
        });
        toast.success('Tag berhasil dibuat');
      }
      
      setTagDialogOpen(false);
      setEditingTag(null);
      fetchCustomerTags();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Tag dengan nama tersebut sudah ada');
      } else {
        toast.error('Gagal menyimpan tag');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTag = async (tag) => {
    if (!window.confirm(`Hapus tag "${tag.name}"? Tag akan dihapus dari semua pelanggan yang memilikinya.`)) return;
    try {
      await api.delete(`/customer-tags/${tag.id}`);
      toast.success('Tag berhasil dihapus');
      fetchCustomerTags();
    } catch (error) {
      toast.error('Gagal menghapus tag');
    }
  };

  const getTagColorClass = (color) => {
    const classes = {
      gray: 'bg-gray-500',
      blue: 'bg-blue-500',
      green: 'bg-green-500',
      yellow: 'bg-yellow-500',
      red: 'bg-red-500',
      purple: 'bg-purple-500',
      orange: 'bg-orange-500',
    };
    return classes[color] || 'bg-gray-500';
  };

  const getTagBadgeClass = (color) => {
    const classes = {
      gray: 'bg-gray-500/10 text-gray-700 border-gray-500/30',
      blue: 'bg-blue-500/10 text-blue-700 border-blue-500/30',
      green: 'bg-green-500/10 text-green-700 border-green-500/30',
      yellow: 'bg-yellow-500/10 text-yellow-700 border-yellow-500/30',
      red: 'bg-red-500/10 text-red-700 border-red-500/30',
      purple: 'bg-purple-500/10 text-purple-700 border-purple-500/30',
      orange: 'bg-orange-500/10 text-orange-700 border-orange-500/30',
    };
    return classes[color] || 'bg-gray-500/10 text-gray-700 border-gray-500/30';
  };

  const handleCreatePrinter = async () => {
    if (!printerForm.name.trim()) {
      toast.error('Printer name is required');
      return;
    }
    if (printerForm.connection_type === 'LAN' && !printerForm.ip_address.trim()) {
      toast.error('IP address is required for LAN printers');
      return;
    }
    
    setLoading(true);
    try {
      const data = {
        ...printerForm,
        outlet_id: currentOutlet.id,
        port: parseInt(printerForm.port) || 9100,
      };
      
      if (editingPrinter) {
        await api.put(`/printers/${editingPrinter.id}`, data);
        toast.success('Printer updated');
      } else {
        await api.post('/printers', data);
        toast.success('Printer created');
      }
      
      setPrinterDialogOpen(false);
      setEditingPrinter(null);
      resetPrinterForm();
      fetchPrinters();
    } catch (error) {
      toast.error('Failed to save printer');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePrinter = async (printerId) => {
    if (!confirm('Delete this printer?')) return;
    
    try {
      await api.delete(`/printers/${printerId}`);
      toast.success('Printer deleted');
      fetchPrinters();
      fetchRoutings();
    } catch (error) {
      toast.error('Failed to delete printer');
    }
  };

  const handleTestPrint = async (printerId) => {
    try {
      await api.post(`/printers/${printerId}/test`);
      toast.success('Test print job created');
      fetchPrintJobs();
    } catch (error) {
      toast.error('Failed to create test print');
    }
  };

  const handleUpdateRouting = async (menuType, printerId) => {
    if (!printerId) return;
    
    try {
      await api.post('/printer-routing', {
        outlet_id: currentOutlet.id,
        menu_type: menuType,
        printer_id: printerId,
      });
      toast.success(`${menuType} routing updated`);
      fetchRoutings();
    } catch (error) {
      toast.error('Failed to update routing');
    }
  };

  const handleRetryPrintJob = async (jobId) => {
    try {
      await api.post(`/print-jobs/${jobId}/retry`);
      toast.success('Print job queued for retry');
      fetchPrintJobs();
    } catch (error) {
      toast.error('Failed to retry print job');
    }
  };

  const openEditPrinter = (printer) => {
    setEditingPrinter(printer);
    setPrinterForm({
      name: printer.name,
      printer_role: printer.printer_role,
      connection_type: printer.connection_type,
      ip_address: printer.ip_address || '',
      port: printer.port || 9100,
      usb_vendor_id: printer.usb_vendor_id || '',
      usb_product_id: printer.usb_product_id || '',
      is_active: printer.is_active,
    });
    setPrinterDialogOpen(true);
  };

  const resetPrinterForm = () => {
    setPrinterForm({
      name: '',
      printer_role: 'KITCHEN',
      connection_type: 'LAN',
      ip_address: '',
      port: 9100,
      usb_vendor_id: '',
      usb_product_id: '',
      is_active: true,
    });
  };

  const getRoutingForMenuType = (menuType) => {
    return routings.find(r => r.menu_type === menuType)?.printer_id || '';
  };

  const getPrintJobStatusIcon = (status) => {
    switch (status) {
      case 'PRINTED':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'FAILED':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'SENT':
        return <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />;
      default:
        return <Clock className="h-4 w-4 text-yellow-500" />;
    }
  };

  // Printer status helpers
  const getPrinterStatusDisplay = (printer) => {
    const status = printer.status || 'UNKNOWN';
    const lastHeartbeat = printer.last_heartbeat_at;
    const heartbeatAge = printer.heartbeat_age_seconds;
    
    let ageText = '';
    if (heartbeatAge !== null && heartbeatAge !== undefined) {
      if (heartbeatAge < 60) {
        ageText = `${heartbeatAge}s ago`;
      } else {
        ageText = `${Math.floor(heartbeatAge / 60)}m ago`;
      }
    }
    
    switch (status) {
      case 'ONLINE':
        return {
          color: 'bg-green-500',
          textColor: 'text-green-500',
          label: t('common.online'),
          subLabel: lastHeartbeat ? `Last seen: ${ageText}` : null,
        };
      case 'OFFLINE':
        return {
          color: 'bg-red-500',
          textColor: 'text-red-500',
          label: t('common.offline'),
          subLabel: printer.last_error || (lastHeartbeat ? `Last seen: ${ageText}` : null),
        };
      default:
        return {
          color: 'bg-yellow-500',
          textColor: 'text-yellow-500',
          label: t('common.unknown'),
          subLabel: t('common.waiting_heartbeat'),
        };
    }
  };

  const handleUpdateOutlet = async () => {
    if (!currentOutlet) return;
    setLoading(true);
    try {
      await api.put(`/outlets/${currentOutlet.id}`, outletForm);
      toast.success('Outlet settings updated');
      fetchOutlets();
    } catch (error) {
      toast.error('Failed to update outlet');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePaymentSettings = async () => {
    if (!currentOutlet) return;
    setLoading(true);
    try {
      await api.put('/settings/payment-mode', paymentSettings, {
        params: { outlet_id: currentOutlet.id },
      });
      toast.success('Payment settings updated');
    } catch (error) {
      toast.error('Failed to update payment settings');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOutlet = async () => {
    if (!newOutletForm.name.trim()) {
      toast.error('Outlet name is required');
      return;
    }
    setLoading(true);
    try {
      await createOutlet({
        ...newOutletForm,
        is_active: true,
      });
      toast.success('New outlet created');
      setNewOutletForm({ name: '', address: '', phone: '' });
    } catch (error) {
      toast.error('Failed to create outlet');
    } finally {
      setLoading(false);
    }
  };

  const canManageOutlets = ['OWNER', 'ADMIN'].includes(user?.role);
  const isOwner = user?.role === 'OWNER';
  const menuItems = getSettingsMenuItems(t, canManageOutlets, isOwner);

  return (
    <div className="flex h-[calc(100vh-64px)] lg:h-screen overflow-hidden" data-testid="settings-page">
      {/* Desktop Sidebar */}
      <SettingsSidebar 
        activeTab={activeTab} 
        onTabChange={setActiveTab} 
        menuItems={menuItems} 
      />

      {/* Main Content Area */}
      <div className="flex-1 overflow-hidden">
        <ScrollArea className="h-full">
          <div className="p-6 space-y-6">
            {/* Header - Mobile shows menu button */}
            <div className="flex items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold">{t('settings.title')}</h1>
                <p className="text-muted-foreground">{t('settings.subtitle')}</p>
              </div>
              <div className="lg:hidden">
                <MobileSettingsSidebar 
                  activeTab={activeTab} 
                  onTabChange={setActiveTab} 
                  menuItems={menuItems}
                  open={mobileMenuOpen}
                  onOpenChange={setMobileMenuOpen}
                />
              </div>
            </div>

            {/* Current Outlet Settings */}
            {activeTab === 'outlet' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Store className="h-5 w-5" />
                    {currentOutlet?.name || t('settings.outlet.name')}
                  </CardTitle>
                  <CardDescription>{t('settings.subtitle')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>{t('settings.outlet.name')}</Label>
                    <Input
                      value={outletForm.name}
                      onChange={(e) => setOutletForm({ ...outletForm, name: e.target.value })}
                      placeholder={t('settings.outlet.name_placeholder')}
                  disabled={!canManageOutlets}
                  data-testid="outlet-name-input"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  {t('settings.outlet.address')}
                </Label>
                <Textarea
                  value={outletForm.address}
                  onChange={(e) => setOutletForm({ ...outletForm, address: e.target.value })}
                  placeholder={t('settings.outlet.address_placeholder')}
                  disabled={!canManageOutlets}
                  rows={3}
                  data-testid="outlet-address-input"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Phone Number
                </Label>
                <Input
                  value={outletForm.phone}
                  onChange={(e) => setOutletForm({ ...outletForm, phone: e.target.value })}
                  placeholder="+62 xxx xxxx xxxx"
                  disabled={!canManageOutlets}
                  data-testid="outlet-phone-input"
                />
              </div>
              
              {canManageOutlets && (
                <>
                  <div className="flex items-center justify-between p-3 bg-muted rounded-md">
                    <div>
                      <p className="font-medium">Active Status</p>
                      <p className="text-sm text-muted-foreground">
                        Inactive outlets won't accept orders
                      </p>
                    </div>
                    <Switch
                      checked={outletForm.is_active}
                      onCheckedChange={(checked) => setOutletForm({ ...outletForm, is_active: checked })}
                      data-testid="outlet-active-switch"
                    />
                  </div>

                  <Button onClick={handleUpdateOutlet} disabled={loading} data-testid="save-outlet-btn">
                    {loading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    Save Changes
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
            )}

            {/* Payment Mode Settings */}
            {activeTab === 'payment' && (
              <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Payment Mode Settings
              </CardTitle>
              <CardDescription>
                Configure prepaid or postpaid mode for different order types
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Dine-in */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-md bg-emerald-500/10">
                    <UtensilsCrossed className="h-5 w-5 text-emerald-500" />
                  </div>
                  <div>
                    <p className="font-medium">{t('settings.payment_mode.dine_in_mode')}</p>
                    <p className="text-sm text-muted-foreground">
                      {paymentSettings.dine_in_mode === 'POSTPAID' 
                        ? t('settings.payment_mode.dine_in_postpaid_desc') 
                        : t('settings.payment_mode.dine_in_prepaid_desc')}
                    </p>
                  </div>
                </div>
                <Select
                  value={paymentSettings.dine_in_mode}
                  onValueChange={(val) => setPaymentSettings({ ...paymentSettings, dine_in_mode: val })}
                >
                  <SelectTrigger className="w-40" data-testid="dine-in-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="POSTPAID">
                      <Badge className="bg-blue-500">{t('pos.postpaid')}</Badge>
                    </SelectItem>
                    <SelectItem value="PREPAID">
                      <Badge className="bg-green-500">{t('pos.prepaid')}</Badge>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Takeaway */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-md bg-blue-500/10">
                    <Package className="h-5 w-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="font-medium">{t('settings.payment_mode.takeaway_mode')}</p>
                    <p className="text-sm text-muted-foreground">
                      {paymentSettings.takeaway_mode === 'PREPAID' 
                        ? t('settings.payment_mode.takeaway_prepaid_desc') 
                        : t('settings.payment_mode.takeaway_postpaid_desc')}
                    </p>
                  </div>
                </div>
                <Select
                  value={paymentSettings.takeaway_mode}
                  onValueChange={(val) => setPaymentSettings({ ...paymentSettings, takeaway_mode: val })}
                >
                  <SelectTrigger className="w-40" data-testid="takeaway-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PREPAID">
                      <Badge className="bg-green-500">{t('pos.prepaid')}</Badge>
                    </SelectItem>
                    <SelectItem value="POSTPAID">
                      <Badge className="bg-blue-500">{t('pos.postpaid')}</Badge>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Delivery */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-md bg-purple-500/10">
                    <Bike className="h-5 w-5 text-purple-500" />
                  </div>
                  <div>
                    <p className="font-medium">{t('settings.payment_mode.delivery_mode')}</p>
                    <p className="text-sm text-muted-foreground">
                      {paymentSettings.delivery_mode === 'PREPAID' 
                        ? t('settings.payment_mode.delivery_prepaid_desc') 
                        : t('settings.payment_mode.delivery_postpaid_desc')}
                    </p>
                  </div>
                </div>
                <Select
                  value={paymentSettings.delivery_mode}
                  onValueChange={(val) => setPaymentSettings({ ...paymentSettings, delivery_mode: val })}
                >
                  <SelectTrigger className="w-40" data-testid="delivery-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PREPAID">
                      <Badge className="bg-green-500">Prepaid</Badge>
                    </SelectItem>
                    <SelectItem value="POSTPAID">
                      <Badge className="bg-blue-500">Postpaid</Badge>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Allow Override */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="font-medium">Allow Cashier Override</p>
                  <p className="text-sm text-muted-foreground">
                    Let cashiers change payment mode per order
                  </p>
                </div>
                <Switch
                  checked={paymentSettings.allow_override}
                  onCheckedChange={(checked) => setPaymentSettings({ ...paymentSettings, allow_override: checked })}
                  data-testid="allow-override-switch"
                />
              </div>

              {canManageOutlets && (
                <Button onClick={handleUpdatePaymentSettings} disabled={loading} data-testid="save-payment-settings">
                  {loading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save Payment Settings
                </Button>
              )}
            </CardContent>
          </Card>
            )}

            {/* Tax & Currency Settings Tab */}
            {canManageOutlets && activeTab === 'tax_currency' && (
              <TaxCurrencySettings api={api} outletId={currentOutlet?.id} />
            )}

            {/* Discount Presets Tab */}
            {canManageOutlets && activeTab === 'discounts' && (
              <div className="space-y-6">
              {/* Discount Settings */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Percent className="h-5 w-5" />
                    Pengaturan Diskon Kasir
                  </CardTitle>
                  <CardDescription>
                    Batasi diskon maksimum yang dapat diberikan oleh kasir
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Maksimum Diskon Persen (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={discountSettings.max_cashier_discount_percent}
                        onChange={(e) => setDiscountSettings({
                          ...discountSettings,
                          max_cashier_discount_percent: parseFloat(e.target.value) || 0,
                        })}
                      />
                    </div>
                    <div>
                      <Label>Maksimum Diskon Nominal (Rp)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={discountSettings.max_cashier_discount_amount}
                        onChange={(e) => setDiscountSettings({
                          ...discountSettings,
                          max_cashier_discount_amount: parseFloat(e.target.value) || 0,
                        })}
                      />
                    </div>
                  </div>
                  <Button onClick={handleSaveDiscountSettings} disabled={loading}>
                    {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    <Save className="h-4 w-4 mr-2" />
                    Simpan Pengaturan
                  </Button>
                </CardContent>
              </Card>

              {/* Discount Presets */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Tag className="h-5 w-5" />
                        Preset Diskon Cepat
                      </CardTitle>
                      <CardDescription>
                        Tombol diskon cepat untuk mempercepat proses kasir
                      </CardDescription>
                    </div>
                    <Button onClick={() => openPresetDialog()} data-testid="add-preset-btn">
                      <Plus className="h-4 w-4 mr-2" />
                      Tambah Preset
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {discountPresets.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">
                      Belum ada preset diskon. Tambahkan preset untuk mempercepat proses kasir.
                    </p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Nama</TableHead>
                          <TableHead>Tipe</TableHead>
                          <TableHead>Nilai</TableHead>
                          <TableHead>Jadwal</TableHead>
                          <TableHead>Warna</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Aksi</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {discountPresets.map((preset) => {
                          const colorClasses = {
                            blue: 'bg-blue-500',
                            orange: 'bg-orange-500',
                            purple: 'bg-purple-500',
                            red: 'bg-red-500',
                            green: 'bg-green-500',
                          };
                          return (
                            <TableRow key={preset.id}>
                              <TableCell>
                                <div>
                                  <span className="font-medium">{preset.name}</span>
                                  <p className="text-xs text-muted-foreground truncate max-w-[150px]">{preset.reason}</p>
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline">
                                  {preset.discount_type === 'PERCENT' ? 'Persen' : 'Nominal'}
                                </Badge>
                              </TableCell>
                              <TableCell className="font-mono">
                                {preset.discount_type === 'PERCENT' 
                                  ? `${preset.value}%` 
                                  : formatCurrency(preset.value)}
                              </TableCell>
                              <TableCell>
                                <span className={`text-xs ${preset.is_scheduled ? 'text-orange-600' : 'text-muted-foreground'}`}>
                                  {formatSchedule(preset)}
                                </span>
                              </TableCell>
                              <TableCell>
                                <div 
                                  className={`w-6 h-6 rounded ${colorClasses[preset.color] || 'bg-gray-500'}`} 
                                  title={preset.color}
                                />
                              </TableCell>
                              <TableCell>
                                <Badge variant={preset.is_active ? 'default' : 'secondary'}>
                                  {preset.is_active ? 'Aktif' : 'Nonaktif'}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => openPresetDialog(preset)}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleDeletePreset(preset)}
                                    className="text-destructive hover:text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>
            )}

            {/* Loyalty Tab */}
            {canManageOutlets && activeTab === 'loyalty' && (
              <div className="space-y-6">
              {/* Loyalty Settings */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Star className="h-5 w-5 text-yellow-500" />
                    Pengaturan Loyalty Program
                  </CardTitle>
                  <CardDescription>
                    Konfigurasi sistem poin dan hadiah untuk pelanggan
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-3 rounded-md border bg-muted/50">
                    <div>
                      <p className="font-medium">Aktifkan Loyalty Program</p>
                      <p className="text-sm text-muted-foreground">Pelanggan akan mendapat poin dari setiap transaksi</p>
                    </div>
                    <Switch
                      checked={loyaltySettings.is_enabled}
                      onCheckedChange={(checked) => setLoyaltySettings({ ...loyaltySettings, is_enabled: checked })}
                    />
                  </div>

                  {loyaltySettings.is_enabled && (
                    <div className="grid grid-cols-2 gap-4 pt-2">
                      <div>
                        <Label>Poin per Rupiah</Label>
                        <Input
                          type="number"
                          min="100"
                          value={loyaltySettings.points_per_amount}
                          onChange={(e) => setLoyaltySettings({
                            ...loyaltySettings,
                            points_per_amount: parseInt(e.target.value) || 1000,
                          })}
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          1 poin per setiap {formatCurrency(loyaltySettings.points_per_amount)}
                        </p>
                      </div>
                      <div>
                        <Label>Multiplier Poin</Label>
                        <Input
                          type="number"
                          min="0.1"
                          step="0.1"
                          value={loyaltySettings.points_multiplier}
                          onChange={(e) => setLoyaltySettings({
                            ...loyaltySettings,
                            points_multiplier: parseFloat(e.target.value) || 1.0,
                          })}
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Untuk promo 2x poin, set ke 2.0
                        </p>
                      </div>
                      <div>
                        <Label>Minimum Transaksi untuk Poin (Rp)</Label>
                        <Input
                          type="number"
                          min="0"
                          value={loyaltySettings.min_transaction_for_points}
                          onChange={(e) => setLoyaltySettings({
                            ...loyaltySettings,
                            min_transaction_for_points: parseInt(e.target.value) || 0,
                          })}
                        />
                      </div>
                      <div>
                        <Label>Masa Berlaku Poin (hari)</Label>
                        <Input
                          type="number"
                          min="0"
                          value={loyaltySettings.points_expiry_days}
                          onChange={(e) => setLoyaltySettings({
                            ...loyaltySettings,
                            points_expiry_days: parseInt(e.target.value) || 365,
                          })}
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          0 = tidak kadaluarsa
                        </p>
                      </div>
                    </div>
                  )}

                  <Button onClick={handleSaveLoyaltySettings} disabled={loading}>
                    {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    <Save className="h-4 w-4 mr-2" />
                    Simpan Pengaturan
                  </Button>
                </CardContent>
              </Card>

              {/* Loyalty Rewards */}
              {loyaltySettings.is_enabled && (
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Gift className="h-5 w-5 text-green-600" />
                          Hadiah yang Dapat Ditukar
                        </CardTitle>
                        <CardDescription>
                          Daftar hadiah yang bisa ditukar dengan poin
                        </CardDescription>
                      </div>
                      <Button onClick={() => openRewardDialog()} data-testid="add-reward-btn">
                        <Plus className="h-4 w-4 mr-2" />
                        Tambah Hadiah
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {loyaltyRewards.length === 0 ? (
                      <p className="text-muted-foreground text-center py-8">
                        Belum ada hadiah. Tambahkan hadiah untuk pelanggan dapat menukar poin.
                      </p>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Hadiah</TableHead>
                            <TableHead>Tipe</TableHead>
                            <TableHead>Nilai</TableHead>
                            <TableHead>Poin</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Aksi</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {loyaltyRewards.map((reward) => (
                            <TableRow key={reward.id}>
                              <TableCell>
                                <div>
                                  <span className="font-medium">{reward.name}</span>
                                  {reward.description && (
                                    <p className="text-xs text-muted-foreground">{reward.description}</p>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline">
                                  {reward.reward_type === 'DISCOUNT_FIXED' && 'Diskon Rp'}
                                  {reward.reward_type === 'DISCOUNT_PERCENT' && 'Diskon %'}
                                  {reward.reward_type === 'FREE_ITEM' && 'Item Gratis'}
                                </Badge>
                              </TableCell>
                              <TableCell className="font-mono">
                                {reward.reward_type === 'DISCOUNT_FIXED' && formatCurrency(reward.reward_value)}
                                {reward.reward_type === 'DISCOUNT_PERCENT' && `${reward.reward_value}%`}
                                {reward.reward_type === 'FREE_ITEM' && (reward.menu_item_name || '-')}
                              </TableCell>
                              <TableCell>
                                <Badge className="bg-yellow-500/10 text-yellow-700 border-yellow-500/30">
                                  <Star className="h-3 w-3 mr-1" />
                                  {reward.points_required}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Badge variant={reward.is_active ? 'default' : 'secondary'}>
                                  {reward.is_active ? 'Aktif' : 'Nonaktif'}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => openRewardDialog(reward)}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleDeleteReward(reward)}
                                    className="text-destructive hover:text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
            )}

            {/* Customer Tags Tab */}
            {canManageOutlets && activeTab === 'tags' && (
              <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Tag className="h-5 w-5 text-purple-600" />
                      Tag Pelanggan
                    </CardTitle>
                    <CardDescription>
                      Kelola tag untuk mengkategorikan pelanggan (VIP, Regular, dll)
                    </CardDescription>
                  </div>
                  <Button onClick={() => openTagDialog()} data-testid="add-tag-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Tambah Tag
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {customerTags.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    Belum ada tag pelanggan. Tambahkan tag untuk mengkategorikan pelanggan Anda.
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tag</TableHead>
                        <TableHead>Deskripsi</TableHead>
                        <TableHead>Diskon Otomatis</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Aksi</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {customerTags.map((tag) => (
                        <TableRow key={tag.id} data-testid={`tag-row-${tag.id}`}>
                          <TableCell>
                            <Badge className={getTagBadgeClass(tag.color)}>
                              <div className={`w-2 h-2 rounded-full ${getTagColorClass(tag.color)} mr-1.5`} />
                              {tag.name}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {tag.description || '-'}
                          </TableCell>
                          <TableCell>
                            {tag.discount_percent > 0 ? (
                              <Badge variant="outline" className="text-green-600">
                                <Percent className="h-3 w-3 mr-1" />
                                {tag.discount_percent}%
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant={tag.is_active ? 'default' : 'secondary'}>
                              {tag.is_active ? 'Aktif' : 'Nonaktif'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openTagDialog(tag)}
                                data-testid={`edit-tag-${tag.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteTag(tag)}
                                className="text-destructive hover:text-destructive"
                                data-testid={`delete-tag-${tag.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
            </CardContent>
            </Card>
            )}

            {/* Printers Tab */}
            {canManageOutlets && activeTab === 'printers' && (
              <div className="space-y-6">
              
              {/* QZ Tray Configuration - PRIMARY */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        {qzConnected ? (
                          <Wifi className="h-5 w-5 text-green-500" />
                        ) : (
                          <WifiOff className="h-5 w-5 text-muted-foreground" />
                        )}
                        QZ Tray Direct Printing
                        <Badge variant={qzConnected ? 'default' : 'secondary'}>
                          {qzConnected ? 'Connected' : 'Offline'}
                        </Badge>
                      </CardTitle>
                      <CardDescription>
                        Direct printing ke thermal printer via QZ Tray (Recommended)
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <QZPrinterConfig />
                </CardContent>
              </Card>

              {/* Legacy Printers List - DEPRECATED */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Printer className="h-5 w-5" />
                        Legacy Thermal Printers
                        <Badge variant="outline" className="text-xs">Deprecated</Badge>
                      </CardTitle>
                      <CardDescription>
                        Konfigurasi printer lama (Python Agent) - Gunakan QZ Tray di atas
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setEditingPrinter(null);
                          setPrinterWizardOpen(true);
                        }}
                        data-testid="printer-wizard-btn"
                      >
                        <Settings2 className="h-4 w-4 mr-2" />
                        Setup Wizard
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setEditingPrinter(null);
                          resetPrinterForm();
                          setPrinterDialogOpen(true);
                        }}
                        data-testid="add-printer-btn"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Manual
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {printers.length === 0 ? (
                    <div className="text-center py-4 text-muted-foreground">
                      <p className="text-sm">No legacy printers configured</p>
                      <p className="text-xs mt-1">Gunakan QZ Tray untuk direct printing</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {printers.map((printer) => {
                        const statusDisplay = getPrinterStatusDisplay(printer);
                        return (
                        <div
                          key={printer.id}
                          className={`p-4 rounded-lg border ${printer.is_active ? '' : 'opacity-50'}`}
                          data-testid={`printer-${printer.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {/* Status indicator dot */}
                              <div className="relative">
                                <div className={`p-2 rounded-md ${
                                  printer.printer_role === 'KITCHEN' ? 'bg-orange-500/10' : 
                                  printer.printer_role === 'BAR' ? 'bg-blue-500/10' : 
                                  'bg-green-500/10'
                                }`}>
                                  {printer.connection_type === 'LAN' ? (
                                    <Wifi className={`h-5 w-5 ${
                                      printer.printer_role === 'KITCHEN' ? 'text-orange-500' : 
                                      printer.printer_role === 'BAR' ? 'text-blue-500' : 
                                      'text-green-500'
                                    }`} />
                                  ) : (
                                    <Usb className={`h-5 w-5 ${
                                      printer.printer_role === 'KITCHEN' ? 'text-orange-500' : 
                                      printer.printer_role === 'BAR' ? 'text-blue-500' : 
                                      'text-green-500'
                                    }`} />
                                  )}
                                </div>
                                {/* Status dot */}
                                <div 
                                  className={`absolute -top-1 -right-1 w-3 h-3 rounded-full ${statusDisplay.color} border-2 border-background`}
                                  title={statusDisplay.label}
                                  data-testid={`printer-status-${printer.id}`}
                                />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium">{printer.name}</p>
                                  <span className={`text-xs font-medium ${statusDisplay.textColor}`}>
                                    {statusDisplay.label}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                  <Badge variant="outline" className={`text-xs ${
                                    printer.printer_role === 'CASHIER' ? 'border-green-500 text-green-600' : ''
                                  }`}>
                                    {printer.printer_role === 'CASHIER' ? 'KASIR' : printer.printer_role}
                                  </Badge>
                                  <span>
                                    {printer.connection_type === 'LAN' 
                                      ? `${printer.ip_address}:${printer.port}` 
                                      : `USB ${printer.usb_vendor_id}:${printer.usb_product_id}`}
                                  </span>
                                </div>
                                {statusDisplay.subLabel && (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {statusDisplay.subLabel}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleTestPrint(printer.id)}
                                data-testid={`test-printer-${printer.id}`}
                              >
                                <Play className="h-4 w-4 mr-1" />
                                Test
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openEditPrinter(printer)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeletePrinter(printer.id)}
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Printer Routing */}
              <Card>
                <CardHeader>
                  <CardTitle>Print Routing</CardTitle>
                  <CardDescription>Map menu types to printers (FOOD → Kitchen, DRINK → Bar)</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* FOOD Routing */}
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-md bg-orange-500/10">
                        <UtensilsCrossed className="h-5 w-5 text-orange-500" />
                      </div>
                      <div>
                        <p className="font-medium">Food Items</p>
                        <p className="text-sm text-muted-foreground">Makanan Utama, Snack, etc.</p>
                      </div>
                    </div>
                    <Select
                      value={getRoutingForMenuType('FOOD')}
                      onValueChange={(val) => handleUpdateRouting('FOOD', val)}
                    >
                      <SelectTrigger className="w-48" data-testid="food-routing">
                        <SelectValue placeholder="Select printer" />
                      </SelectTrigger>
                      <SelectContent>
                        {printers.filter(p => p.is_active).map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* DRINK Routing */}
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-md bg-blue-500/10">
                        <Package className="h-5 w-5 text-blue-500" />
                      </div>
                      <div>
                        <p className="font-medium">Drink Items</p>
                        <p className="text-sm text-muted-foreground">Minuman, Dessert, etc.</p>
                      </div>
                    </div>
                    <Select
                      value={getRoutingForMenuType('DRINK')}
                      onValueChange={(val) => handleUpdateRouting('DRINK', val)}
                    >
                      <SelectTrigger className="w-48" data-testid="drink-routing">
                        <SelectValue placeholder="Select printer" />
                      </SelectTrigger>
                      <SelectContent>
                        {printers.filter(p => p.is_active).map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              {/* Customer Receipt Settings */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Receipt className="h-5 w-5" />
                    Customer Receipt Settings
                  </CardTitle>
                  <CardDescription>Configure receipt printing for customers</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Receipt Printer Selection */}
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-md bg-green-500/10">
                        <Printer className="h-5 w-5 text-green-500" />
                      </div>
                      <div>
                        <p className="font-medium">Receipt Printer</p>
                        <p className="text-sm text-muted-foreground">Printer untuk struk pelanggan</p>
                      </div>
                    </div>
                    <Select
                      value={receiptSettings.receipt_printer_id || 'none'}
                      onValueChange={(val) => setReceiptSettings({ ...receiptSettings, receipt_printer_id: val === 'none' ? null : val })}
                    >
                      <SelectTrigger className="w-48" data-testid="receipt-printer-select">
                        <SelectValue placeholder="Select printer" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {printers.filter(p => p.is_active).map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Auto Print Toggle */}
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <p className="font-medium">Auto-print Receipt</p>
                      <p className="text-sm text-muted-foreground">
                        Otomatis cetak struk setelah pembayaran selesai
                      </p>
                    </div>
                    <Switch
                      checked={receiptSettings.auto_print_on_payment}
                      onCheckedChange={(checked) => setReceiptSettings({ ...receiptSettings, auto_print_on_payment: checked })}
                      data-testid="auto-print-receipt-switch"
                    />
                  </div>

                  {/* Footer Text */}
                  <div>
                    <Label>Footer Text</Label>
                    <Input
                      value={receiptSettings.footer_text}
                      onChange={(e) => setReceiptSettings({ ...receiptSettings, footer_text: e.target.value })}
                      placeholder="Terima kasih atas kunjungan Anda!"
                      data-testid="receipt-footer-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Pesan di bagian bawah struk</p>
                  </div>

                  <Button onClick={handleUpdateReceiptSettings} disabled={loading} data-testid="save-receipt-settings">
                    {loading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    Save Receipt Settings
                  </Button>
                </CardContent>
              </Card>

              {/* Print Jobs History */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Recent Print Jobs</CardTitle>
                      <CardDescription>Monitor and retry failed prints</CardDescription>
                    </div>
                    <Button variant="outline" size="sm" onClick={fetchPrintJobs}>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Refresh
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {printJobs.length === 0 ? (
                    <p className="text-center py-4 text-muted-foreground">No print jobs yet</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Status</TableHead>
                          <TableHead>Order</TableHead>
                          <TableHead>Printer</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Time</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {printJobs.map((job) => (
                          <TableRow key={job.id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {getPrintJobStatusIcon(job.status)}
                                <span className={
                                  job.status === 'PRINTED' ? 'text-green-600' :
                                  job.status === 'FAILED' ? 'text-red-600' :
                                  job.status === 'SENT' ? 'text-blue-600' : 'text-yellow-600'
                                }>
                                  {job.status}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="font-mono text-sm">{job.order_number}</TableCell>
                            <TableCell>{job.printer_name}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{job.menu_type}</Badge>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {new Date(job.created_at).toLocaleTimeString()}
                            </TableCell>
                            <TableCell>
                              {job.status === 'FAILED' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRetryPrintJob(job.id)}
                                  data-testid={`retry-job-${job.id}`}
                                >
                                  <RefreshCw className="h-4 w-4 mr-1" />
                                  Retry
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              {/* Local Agent Info */}
              <Card>
                <CardHeader>
                  <CardTitle>Local Print Agent</CardTitle>
                  <CardDescription>Instructions for setting up the print agent on your local machine</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-muted rounded-lg">
                    <p className="font-medium mb-2">Your Outlet ID:</p>
                    <code className="block p-2 bg-background rounded border text-sm font-mono break-all" data-testid="outlet-id-display">
                      {currentOutlet?.id}
                    </code>
                  </div>
                  <div className="text-sm text-muted-foreground space-y-2">
                    <p>1. Download the print agent from the <code>/print_agent</code> folder</p>
                    <p>2. Install dependencies: <code>pip install -r requirements.txt</code></p>
                    <p>3. Configure <code>.env</code> with the Outlet ID above</p>
                    <p>4. Run: <code>python print_agent.py</code></p>
                  </div>
                </CardContent>
              </Card>

              {/* Printer Setup Wizard Dialog */}
              <PrinterSetupWizard
                outletId={currentOutlet?.id}
                open={printerWizardOpen}
                onOpenChange={setPrinterWizardOpen}
                existingPrinter={editingPrinter}
                onPrinterSaved={() => {
                  fetchPrinters();
                  setEditingPrinter(null);
                }}
              />
            </div>
            )}

            {/* Language Settings */}
            {activeTab === 'language' && (
              <LanguageSwitcher />
            )}

            {/* Translation Management */}
            {canManageOutlets && activeTab === 'translations' && (
              <TranslationSettings />
            )}

            {/* Backup & Restore */}
            {canManageOutlets && activeTab === 'backup' && (
              <BackupRestoreSettings />
            )}

            {/* All Outlets */}
            {canManageOutlets && activeTab === 'outlets' && (
              <Card>
              <CardHeader>
                <CardTitle>All Outlets</CardTitle>
                <CardDescription>View and manage all restaurant outlets</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {outlets.map((outlet) => (
                    <div
                      key={outlet.id}
                      className={`p-4 rounded-md border ${outlet.id === currentOutlet?.id ? 'border-primary bg-primary/5' : ''}`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold flex items-center gap-2">
                            <Store className="h-4 w-4" />
                            {outlet.name}
                            {outlet.id === currentOutlet?.id && (
                              <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded">
                                Current
                              </span>
                            )}
                          </h3>
                          {outlet.address && (
                            <p className="text-sm text-muted-foreground mt-1">{outlet.address}</p>
                          )}
                          {outlet.phone && (
                            <p className="text-sm text-muted-foreground">{outlet.phone}</p>
                          )}
                        </div>
                        <span className={`text-xs px-2 py-1 rounded ${outlet.is_active ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'}`}>
                          {outlet.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            )}

            {/* Create New Outlet */}
            {canManageOutlets && activeTab === 'new' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Plus className="h-5 w-5" />
                    Create New Outlet
                  </CardTitle>
                  <CardDescription>Add a new branch or outlet location</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Outlet Name *</Label>
                    <Input
                      value={newOutletForm.name}
                      onChange={(e) => setNewOutletForm({ ...newOutletForm, name: e.target.value })}
                      placeholder="e.g., Dapoer Bengawan - Branch 2"
                      data-testid="new-outlet-name-input"
                    />
                  </div>
                  <div>
                    <Label>Address</Label>
                    <Textarea
                      value={newOutletForm.address}
                      onChange={(e) => setNewOutletForm({ ...newOutletForm, address: e.target.value })}
                      placeholder={t('form.enter_full_address')}
                      rows={3}
                      data-testid="new-outlet-address-input"
                    />
                  </div>
                  <div>
                    <Label>Phone Number</Label>
                    <Input
                      value={newOutletForm.phone}
                      onChange={(e) => setNewOutletForm({ ...newOutletForm, phone: e.target.value })}
                      placeholder="+62 xxx xxxx xxxx"
                      data-testid="new-outlet-phone-input"
                    />
                  </div>
                  <Button onClick={handleCreateOutlet} disabled={loading} data-testid="create-outlet-btn">
                    {loading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4 mr-2" />
                    )}
                    Create Outlet
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Update System - OWNER only */}
            {isOwner && activeTab === 'update' && (
              <UpdateSettings />
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Printer Dialog */}
      <Dialog open={printerDialogOpen} onOpenChange={setPrinterDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingPrinter ? 'Edit Printer' : 'Add New Printer'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Printer Name *</Label>
              <Input
                value={printerForm.name}
                onChange={(e) => setPrinterForm({ ...printerForm, name: e.target.value })}
                placeholder="e.g., Kitchen Printer"
                data-testid="printer-name-input"
              />
            </div>
            
            <div>
              <Label>Printer Role</Label>
              <Select
                value={printerForm.printer_role}
                onValueChange={(val) => setPrinterForm({ ...printerForm, printer_role: val })}
              >
                <SelectTrigger data-testid="printer-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="KITCHEN">Kitchen (Food)</SelectItem>
                  <SelectItem value="BAR">Bar (Drinks)</SelectItem>
                  <SelectItem value="CASHIER">Kasir (All)</SelectItem>
                </SelectContent>
              </Select>
              {printerForm.printer_role === 'CASHIER' && (
                <p className="text-xs text-muted-foreground mt-1 text-amber-600">
                  Printer ini digunakan untuk struk pelanggan (kasir). Tidak menerima tiket dapur.
                </p>
              )}
            </div>

            <div>
              <Label>Connection Type</Label>
              <Select
                value={printerForm.connection_type}
                onValueChange={(val) => setPrinterForm({ ...printerForm, connection_type: val })}
              >
                <SelectTrigger data-testid="connection-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="LAN">
                    <div className="flex items-center gap-2">
                      <Wifi className="h-4 w-4" />
                      LAN (Network)
                    </div>
                  </SelectItem>
                  <SelectItem value="USB">
                    <div className="flex items-center gap-2">
                      <Usb className="h-4 w-4" />
                      USB (Direct)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {printerForm.connection_type === 'LAN' && (
              <>
                <div>
                  <Label>IP Address *</Label>
                  <Input
                    value={printerForm.ip_address}
                    onChange={(e) => setPrinterForm({ ...printerForm, ip_address: e.target.value })}
                    placeholder="192.168.1.100"
                    data-testid="printer-ip-input"
                  />
                </div>
                <div>
                  <Label>Port</Label>
                  <Input
                    type="number"
                    value={printerForm.port}
                    onChange={(e) => setPrinterForm({ ...printerForm, port: e.target.value })}
                    placeholder="9100"
                    data-testid="printer-port-input"
                  />
                </div>
              </>
            )}

            {printerForm.connection_type === 'USB' && (
              <>
                <div>
                  <Label>USB Vendor ID</Label>
                  <Input
                    value={printerForm.usb_vendor_id}
                    onChange={(e) => setPrinterForm({ ...printerForm, usb_vendor_id: e.target.value })}
                    placeholder="0x04b8"
                    data-testid="printer-vendor-input"
                  />
                </div>
                <div>
                  <Label>USB Product ID</Label>
                  <Input
                    value={printerForm.usb_product_id}
                    onChange={(e) => setPrinterForm({ ...printerForm, usb_product_id: e.target.value })}
                    placeholder="0x0202"
                    data-testid="printer-product-input"
                  />
                </div>
              </>
            )}

            <div className="flex items-center justify-between p-3 bg-muted rounded-md">
              <div>
                <p className="font-medium">Active</p>
                <p className="text-sm text-muted-foreground">Enable this printer</p>
              </div>
              <Switch
                checked={printerForm.is_active}
                onCheckedChange={(checked) => setPrinterForm({ ...printerForm, is_active: checked })}
                data-testid="printer-active-switch"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPrinterDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreatePrinter} disabled={loading} data-testid="save-printer-btn">
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {editingPrinter ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Discount Preset Dialog */}
      <Dialog open={discountDialogOpen} onOpenChange={setDiscountDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5 text-orange-600" />
              {editingPreset ? 'Edit Preset Diskon' : 'Tambah Preset Diskon'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Nama Preset *</Label>
              <Input
                value={presetForm.name}
                onChange={(e) => setPresetForm({ ...presetForm, name: e.target.value })}
                placeholder="Contoh: Member 10%"
                data-testid="preset-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipe Diskon</Label>
                <Select 
                  value={presetForm.discount_type} 
                  onValueChange={(v) => setPresetForm({ ...presetForm, discount_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PERCENT">Persen (%)</SelectItem>
                    <SelectItem value="FIXED">Nominal (Rp)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Nilai {presetForm.discount_type === 'PERCENT' ? '(%)' : '(Rp)'} *</Label>
                <Input
                  type="number"
                  min="0"
                  max={presetForm.discount_type === 'PERCENT' ? 100 : undefined}
                  value={presetForm.value}
                  onChange={(e) => setPresetForm({ ...presetForm, value: e.target.value })}
                  placeholder={presetForm.discount_type === 'PERCENT' ? '10' : '5000'}
                  data-testid="preset-value-input"
                />
              </div>
            </div>
            
            <div>
              <Label>Alasan Default *</Label>
              <Input
                value={presetForm.reason}
                onChange={(e) => setPresetForm({ ...presetForm, reason: e.target.value })}
                placeholder="Contoh: Diskon member"
                data-testid="preset-reason-input"
              />
            </div>
            
            <div>
              <Label>Warna Tombol</Label>
              <Select 
                value={presetForm.color} 
                onValueChange={(v) => setPresetForm({ ...presetForm, color: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="blue">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-blue-500" />
                      Biru
                    </div>
                  </SelectItem>
                  <SelectItem value="orange">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-orange-500" />
                      Orange
                    </div>
                  </SelectItem>
                  <SelectItem value="purple">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-purple-500" />
                      Ungu
                    </div>
                  </SelectItem>
                  <SelectItem value="red">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-red-500" />
                      Merah
                    </div>
                  </SelectItem>
                  <SelectItem value="green">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-green-500" />
                      Hijau
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Schedule Section */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-sm font-medium">Jadwal Aktif</Label>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={presetForm.is_scheduled}
                    onCheckedChange={(checked) => setPresetForm({ ...presetForm, is_scheduled: checked })}
                  />
                  <span className="text-xs text-muted-foreground">
                    {presetForm.is_scheduled ? 'Terjadwal' : 'Selalu aktif'}
                  </span>
                </div>
              </div>
              
              {presetForm.is_scheduled && (
                <div className="space-y-3 bg-muted/50 p-3 rounded-md">
                  {/* Days Selection */}
                  <div>
                    <Label className="text-xs text-muted-foreground">Hari</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {dayNames.map((day, idx) => (
                        <Button
                          key={idx}
                          type="button"
                          variant={presetForm.schedule_days.includes(idx) ? 'default' : 'outline'}
                          size="sm"
                          className="h-7 text-xs px-2"
                          onClick={() => toggleScheduleDay(idx)}
                        >
                          {day.substring(0, 3)}
                        </Button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Time Range */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-muted-foreground">Mulai</Label>
                      <Input
                        type="time"
                        value={presetForm.schedule_start_time}
                        onChange={(e) => setPresetForm({ ...presetForm, schedule_start_time: e.target.value })}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Sampai</Label>
                      <Input
                        type="time"
                        value={presetForm.schedule_end_time}
                        onChange={(e) => setPresetForm({ ...presetForm, schedule_end_time: e.target.value })}
                        className="h-8 text-sm"
                      />
                    </div>
                  </div>
                  
                  <p className="text-xs text-muted-foreground">
                    Preset hanya akan muncul pada hari dan jam yang dipilih (zona waktu WIB).
                  </p>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiscountDialogOpen(false)}>
              Batal
            </Button>
            <Button onClick={handleSavePreset} disabled={loading}>
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingPreset ? 'Simpan Perubahan' : 'Tambah Preset'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Loyalty Reward Dialog */}
      <Dialog open={rewardDialogOpen} onOpenChange={setRewardDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-green-600" />
              {editingReward ? 'Edit Hadiah' : 'Tambah Hadiah'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Nama Hadiah *</Label>
              <Input
                value={rewardForm.name}
                onChange={(e) => setRewardForm({ ...rewardForm, name: e.target.value })}
                placeholder="Contoh: Diskon 5K, Es Teh Gratis"
                data-testid="reward-name-input"
              />
            </div>
            
            <div>
              <Label>Deskripsi</Label>
              <Input
                value={rewardForm.description}
                onChange={(e) => setRewardForm({ ...rewardForm, description: e.target.value })}
                placeholder="Deskripsi singkat"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipe Hadiah</Label>
                <Select 
                  value={rewardForm.reward_type} 
                  onValueChange={(v) => setRewardForm({ ...rewardForm, reward_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DISCOUNT_FIXED">Diskon Nominal (Rp)</SelectItem>
                    <SelectItem value="DISCOUNT_PERCENT">Diskon Persen (%)</SelectItem>
                    <SelectItem value="FREE_ITEM">Item Gratis</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Poin Dibutuhkan *</Label>
                <Input
                  type="number"
                  min="1"
                  value={rewardForm.points_required}
                  onChange={(e) => setRewardForm({ ...rewardForm, points_required: e.target.value })}
                  placeholder="50"
                  data-testid="reward-points-input"
                />
              </div>
            </div>
            
            {rewardForm.reward_type === 'DISCOUNT_FIXED' && (
              <div>
                <Label>Nilai Diskon (Rp)</Label>
                <Input
                  type="number"
                  min="0"
                  value={rewardForm.reward_value}
                  onChange={(e) => setRewardForm({ ...rewardForm, reward_value: e.target.value })}
                  placeholder="5000"
                />
              </div>
            )}
            
            {rewardForm.reward_type === 'DISCOUNT_PERCENT' && (
              <div>
                <Label>Persentase Diskon (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={rewardForm.reward_value}
                  onChange={(e) => setRewardForm({ ...rewardForm, reward_value: e.target.value })}
                  placeholder="10"
                />
              </div>
            )}
            
            {rewardForm.reward_type === 'FREE_ITEM' && (
              <div>
                <Label>Nama Item Gratis</Label>
                <Input
                  value={rewardForm.menu_item_name}
                  onChange={(e) => setRewardForm({ ...rewardForm, menu_item_name: e.target.value })}
                  placeholder="Contoh: Es Teh Manis"
                />
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setRewardDialogOpen(false)}>
              Batal
            </Button>
            <Button onClick={handleSaveReward} disabled={loading} className="bg-green-600 hover:bg-green-700">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingReward ? 'Simpan Perubahan' : 'Tambah Hadiah'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Tag Dialog */}
      <Dialog open={tagDialogOpen} onOpenChange={setTagDialogOpen}>
        <DialogContent className="max-w-md" data-testid="tag-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5 text-purple-600" />
              {editingTag ? 'Edit Tag' : 'Tambah Tag Baru'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Nama Tag *</Label>
              <Input
                value={tagForm.name}
                onChange={(e) => setTagForm({ ...tagForm, name: e.target.value })}
                placeholder="Contoh: VIP, Regular, Member"
                data-testid="tag-name-input"
              />
            </div>
            
            <div>
              <Label>Warna</Label>
              <Select 
                value={tagForm.color} 
                onValueChange={(v) => setTagForm({ ...tagForm, color: v })}
              >
                <SelectTrigger data-testid="tag-color-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {tagColors.map((color) => (
                    <SelectItem key={color} value={color}>
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded ${getTagColorClass(color)}`} />
                        {color.charAt(0).toUpperCase() + color.slice(1)}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Deskripsi</Label>
              <Input
                value={tagForm.description}
                onChange={(e) => setTagForm({ ...tagForm, description: e.target.value })}
                placeholder="Deskripsi singkat (opsional)"
                data-testid="tag-description-input"
              />
            </div>
            
            <div>
              <Label>Diskon Otomatis (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={tagForm.discount_percent}
                onChange={(e) => setTagForm({ ...tagForm, discount_percent: e.target.value })}
                placeholder="0"
                data-testid="tag-discount-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Diskon yang otomatis diterapkan untuk pelanggan dengan tag ini
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setTagDialogOpen(false)}>
              Batal
            </Button>
            <Button onClick={handleSaveTag} disabled={loading} className="bg-purple-600 hover:bg-purple-700">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingTag ? 'Simpan Perubahan' : 'Tambah Tag'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
